<template>
  <!------------------------------------------
    Map for the print version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-district-print-map">
    <div>
      <MapTemplate :showLayerPanel="false" :mapContainerId="mapContainerId" :loading="loading" mapHeight="18rem" />
    </div>
  </div>
</template>

<script>
import MapTemplate from '@/components/map/MapTemplate'
import mapboxgl from "mapbox-gl"
import { mergedBBox } from '@/js/map_utils.js'
import states from '@/js/stFips.js'

let map

export default {
  name: 'CDProfilesPrintMap',

  components: { MapTemplate },

  props: {
    selectedCDFips: { type: String, required: true },
    colorPerServArea: { type: Array, required: true },
    officesPerGrantee: { type: Object, required: true },
    mapboxStyle: { type: String, required: false, default: "mapbox://styles/lsc-odga/cl8sv2oq8003i15p90nywwsu6?optimize=true" },
    interactive: { type: Boolean, required: false, default: true },
    stateName: { type: String, required: true }
  },

  data () {
    return {
      loaded: false,
      newBBox: null, // When changing the bounding boxes, set the new bounding box to be newBBox to automatically re-fit the map,
      // canvasBounds: null,
      defaultBoundsPadding: 30,
      accessToken: `${process.env.VUE_APP_MAPBOX_TOKEN}`,
      map: null,
      defaultBBox: [-124.736342, 24.521208, -66.945392, 49.382808], // contiguous US (used if no bbox provided)
      allowScrollZoom: false,
      loading: false,
      mapContainerId: 'map-container',
      layers: [
        { 
          tilesetId: 'lsc-odga.2021_states',
          loadOnStart: true,
          layerDefs: {
            'id': '2021_states-fill',
            'type': 'fill',
            'source': `2021_states-fill-src`,
            'source-layer': '2021_states',
            'paint': {
              'fill-color': '#eee'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.2021_states',
          loadOnStart: true,
          layerDefs: {
            'id': '2021_states',
            'type': 'line',
            'source': `2021_states-src`,
            'source-layer': '2021_states',
            'paint': {
              'line-width': 2,
              'line-color': '#ddd'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.2024_bf_general_v2',
          loadOnStart: true,
          filterOn: 'st_abbrev',
          layerDefs: {
            'id': '2024_bf_general_v2-fill',
            'type': 'fill',
            'source': `2024_bf_general_v2-fill-src`,
            'source-layer': '2024_bf_general_v2',
            'paint': {
              'fill-opacity': 0.7,
              'fill-color': '#165991',
              'fill-outline-color': 'rgba(0,0,0,0)'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.counties_2024_v2',
          loadOnStart: true,
          filterOn: 'st_abbrev',
          layerDefs: {
            'id': 'counties_2024_v2',
            'type': 'line',
            'source': `counties_2024_v2-src`,
            'source-layer': 'counties_2024_v2',
            'paint': {
              'line-width': 0.2,
              'line-color': '#f6f6f6'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.119th_congress',
          loadOnStart: true,
          filterOn: 'geoid',
          layerDefs: {
            'id': '119th_congress-fill',
            'type': 'fill',
            'source': `119th_congress-fill-src`,
            'source-layer': '119th_congress',
            'paint': {
              'fill-opacity': 0,
              'fill-color': '#f6f6f6',
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.119th_congress',
          loadOnStart: true,
          filterOn: 'geoid',
          layerDefs: {
            'id': '119th_congress',
            'type': 'line',
            'source': `119th_congress-src`,
            'source-layer': '119th_congress',
            'paint': {
              'line-width': 2.5,
              'line-color': 'black'
            }
          }
        }
      ]
    }
  },

  computed: {
    bbox () { return this.newBBox === null || this.newBBox === undefined ? this.defaultBBox : this.newBBox },
    serviceAreaIds() { return this.colorPerServArea.map(x => x.area) },
    paddingOrDefault () { return this.padding === null || this.padding === undefined ? this.defaultBoundsPadding : this.padding },
    stateFips () {
      return this.selectedCDFips.substring(0,2)
    },
    stateAbbrev () {
      return states.filter(x => x.fips === this.stateFips)[0].st
    },
    filterCDField () { 
      return this.selectedCDFips !== '' ? ["==", "geoid", this.selectedCDFips] : ['has', 'placeholder'] 
    },
    filterStateAbbrev () { 
      return this.selectedCDFips !== '' ? ["==", "st_abbrev", this.stateAbbrev] : ['has', 'placeholder'] 
    },
    servAreaColorExpression () {
      let exp = ['match', ['get', 'serv_area']]
      this.colorPerServArea.forEach(function (d) {
        exp.push(
          d['area'],
          d['color']
        )
      })
      exp.push("rgba(0,0,0,0)") // default if no match
      return exp
    },
    // stateColorExpression () {
    //   let exp = ['match', ['get', 'st_abbrev']]
    //   let abbrev = this.stateAbbrev
    //   states.forEach(function(d) { 
    //     if (d.st === abbrev) {
    //       exp.push( d.st, '#f9f9f9' )
    //     } else {
    //       exp.push( d.st, '#f3f3f3' )
    //     }
    //   })
    //   exp.push('#ddd')
    //   return exp
    // }
  },

  created () {
  },
  
  async mounted () {
    // Initialize the map
    mapboxgl.accessToken = this.accessToken
    this.$nextTick(() => {
      this.mapSetup()
    })

    if (this.selectedCDFips && this.serviceAreaIds.length > 0) {
      this.newBBox = await this.getCDBounds()
      // this.newBBox = await this.getAndCombineBounds(this.serviceAreaIds)
    }
  },

  methods: {
    mapSetup: function() { 
      this.loading = true
      let padding = this.paddingOrDefault,
          allowScrollZoom = this.allowScrollZoom,
          mapContainerId = this.mapContainerId,
          component = this

      map = new mapboxgl.Map({
        container: mapContainerId,
        style: this.mapboxStyle,
        interactive: this.interactive,
        preserveDrawingBuffer: true
      })

      map.fitBounds( component.bbox, { padding: padding, duration:0 })

      if (allowScrollZoom === false) {
        map.scrollZoom.disable()
      }

      // ZOOM CONTROLS
      map.addControl(new mapboxgl.FullscreenControl({container: document.querySelector('#' + mapContainerId)}), 'top-left')
      const nav = new mapboxgl.NavigationControl({
        visualizePitch: true
      })
      map.addControl(nav, 'top-left')

      // LAYERS
      map.on('load', function () {
        if (component.layers.length > 0) {
          component.initLayers()
        }

        setTimeout(() => { 
          component.loading = false 
          component.loaded = true
        }, 100)
      })
    },

    async initLayers () {
      this.layers.forEach(layer => {
        if (layer.loadOnStart === true && map.getSource(layer.layerDefs['source']) === undefined) {
          map.addSource(`${layer.layerDefs['source']}`, {
            type: 'vector',
            url: `mapbox://${layer.tilesetId}`
          })
          map.addLayer(layer.layerDefs)
        }
      })
      this.filterLayers()
    },

    fitBounds (bbox) {
      if (map) {
        let padding = this.paddingOrDefault
        map.fitBounds( bbox, { padding: padding, duration:0 })
        // this.calculateCanvasBounds()
      }
    },

    filterLayers () {
      const servAreaColorExp = this.servAreaColorExpression
      // const stateColorExp = this.stateColorExpression
      if (map) {
        this.layers.forEach(layer => {
          try {
            // FILTER
            if (layer.filterOn === 'geoid') {
              layer.layerDefs['filter'] = this.filterCDField
              map.setFilter(layer.layerDefs['id'], this.filterCDField)
            }
            if (layer.filterOn === 'st_abbrev') {
              layer.layerDefs['filter'] = this.filterStateAbbrev
              map.setFilter(layer.layerDefs['id'], this.filterStateAbbrev)
            }
            // COLOR
            if (layer.layerDefs['id'] === '2024_bf_general_v2-fill' && this.colorPerServArea.length > 0) {
              map.setPaintProperty(layer.layerDefs['id'], 'fill-color', servAreaColorExp)
            }
          } catch (err) {
            console.log(err)
          }
        })
      }
    },

    hideMapElementsForPrint () {
      const elements = document.querySelectorAll('.mapboxgl-control-container');
      elements.forEach(el => { el.remove() })
    },

    async addOffices () {

      let officesWithDistrictInfo = await this.getOfficesInDistrict()

      // const numGrantees = Object.keys(officesWithDistrictInfo).length
      // const isSingleGrantee = numGrantees === 1 ? true : false
      // const officeDotSize = isSingleGrantee === true ? 0.5 : 0
            
      for (const rno of Object.keys(officesWithDistrictInfo)) {
        let o = officesWithDistrictInfo[rno]
        o.features = o.features.filter(x => x.properties.state === this.stateName)
        let s = `offices_${rno}`

        // Remove the layer if already exists from previous draw
        const alreadyExists = map.getLayer(s)
        if(typeof alreadyExists !== 'undefined') {
          map.removeLayer(s).removeSource(s);
        }

        if (o !== null && o !== undefined && map.getSource(s) === undefined) {
          map.addSource(s, {
            'type': 'geojson',
            'data': o
          })

          map.addLayer({
            'id': s,
            'type': 'symbol',
            'source': s,
            'layout': {
              'icon-image': 'Main',
              'icon-size': ['match', ['get', 'inCD'], 'true', 0.8, 0.6],
              'icon-allow-overlap': true
            }
          })
        }
      }
    },

    async removeOffices (rnos) {
      // Remove offices from prior map loads
      for (const rno of rnos) {
        let s = `offices_${rno}`
        const alreadyExists = map.getLayer(s)
        if(typeof alreadyExists !== 'undefined') {
          map.removeLayer(s).removeSource(s)
        }
      }
    },

    async getCDBounds () {
      const resp = await fetch(`${process.env.VUE_APP_BACKEND_NEW_DATA_API}/public/spatial/bounds/congressional_districts/119th/${this.selectedCDFips}/bounds.json`)
        .then(function (response) {
          return response.json()
        })
        .catch((err) => { 
          console.log(err) 
        })
        return resp['bbox']
    },

    async getAndCombineBounds (servAreasArray) {
      /**
       * Fetches the bounding boxes for each service area in the serviceAreaIds array, then combines the bounding boxes into one that encompasses all.
       * 
       * @param {array} servAreasArray  Array of service area IDs, like ['AZ-1'] or ['AZ-1', 'AZ-2'].
       * @return {array}  Returns an array of coordinates (bounding box).
       */

      let isHawaii = this.stateFips === '15'

      let x = Promise.all(servAreasArray.map(async x => {
        const resp = await fetch(`${process.env.VUE_APP_BACKEND_NEW_DATA_API}/public/spatial/bounds/service_areas/2024/${x}/bounds.json`)
        .then(function (response) {
          return response.json()
        })
        .catch((err) => { 
          console.log(err) 
        })
        return resp['bbox']
      }))
      
      let newBBox = await x.then(function(values) {
        // Override for Hawaii to remove tiny islands
        if (isHawaii) {
          return [-160.2471, 18.910361, -154.806773, 22.2356] // [-178.33471,18.91036,-154.80679,28.40213]
        }
        return mergedBBox(values)
      })
      .catch((err) => console.log(err))
      return newBBox
    },

    checkForCdFeatures () {
      return new Promise((ok, fail) => { 
          setTimeout(() => 
            map.queryRenderedFeatures().length > 0 && map.queryRenderedFeatures().filter(x => x.layer.id === '119th_congress').length > 0 === true ? 
            ok(true) : 
            fail("Conrgressional district layer is not yet ready")
          , 100)
      })    
    },
    
    async waitForCdLayer (tries = 20) {
      if (tries > 0) {
        const result = await this.checkForCdFeatures().catch(e => e)
        if (result === "Conrgressional district layer is not yet ready") { return await this.waitForCdLayer(tries - 1) }             
        return result
      }
      return `Failed to identify congressional district layer is ready`
    },

    async getOfficesInDistrict () {
      let offices = this.officesPerGrantee
      let cd = this.selectedCDFips
      let cityNamesInCd = [] // Keep track of city names already labeled as within the district, to only show labels for one of them
      let allOfficesInView = {}

      let officesEnhanced = this.waitForCdLayer().then(() => {
        // console.log('Congressional districts ready for offices = ', x)
        for (const rno of Object.keys(offices)) {
          let officeFeaturesInState = offices[rno].features.filter(x => x.properties.state === this.stateName)

          // For each office determine if it is A) in the map view, B) within the district, and C) on which side of the view for label positioning
          for (const f of officeFeaturesInState) {

            let inView = this.officeIsInView(f.geometry.coordinates)

            f.properties.officeInView = inView
            if (inView === true) {
              if (Object.keys(allOfficesInView).indexOf(rno) === -1) {
                allOfficesInView[rno] = []
              }
              allOfficesInView[rno].push(f)
            }

            let inLayers = map.queryRenderedFeatures( map.project(f.geometry.coordinates) )
            let cityInCD = inLayers.filter(x => x.layer.id === '119th_congress-fill' && x.properties.geoid === cd).length > 0 ? true : false
            
            if (cityInCD === true) {
              f.properties.inCD = 'true'
              if (cityNamesInCd.indexOf(f.properties.city) === -1) {
                cityNamesInCd.push(f.properties.city)
              }
            } else {
              f.properties.inCD = 'false'
            }
          }
        }
        this.$emit('officesInView', allOfficesInView)
        return offices
      })
      return officesEnhanced
    },

    // calculateCanvasBounds() {
    //   // https://stackoverflow.com/questions/50490306/how-to-get-mapbox-gl-map-viewport-edges-coordinates-considering-pitch-and-bearin
    //   const canvas = map.getCanvas()
    //   const w = canvas.width / window.devicePixelRatio
    //   const h = canvas.height / window.devicePixelRatio
    //   const cUL = map.unproject ([0,0]).toArray()
    //   const cUR = map.unproject ([w,0]).toArray()
    //   const cLR = map.unproject ([w,h]).toArray()
    //   const cLL = map.unproject ([0,h]).toArray()
    //   const coordinates = [cUL,cUR,cLR,cLL]
    //   this.canvasBounds = coordinates
    // },

    officeIsInView (officeCoords) {
      return map.getBounds().contains(officeCoords) 
    },

    // getAnchorPos (officeCoords) {
    //   const horizontalMid = (this.canvasBounds[1][0] + this.canvasBounds[0][0]) / 2
    //   const verticalMid = (this.canvasBounds[1][1] + this.canvasBounds[2][1]) / 2      
    //   if (officeCoords[0] > horizontalMid) { return 'right' }
    //   else { return 'left' }
    // }
  },

  watch: {
    newBBox(to) {
      this.fitBounds(to)
    },
    loaded (to) {
      if (to === true) {
        this.hideMapElementsForPrint()
        
        if (Object.keys(this.officesPerGrantee).length > 0) {
          this.addOffices()
        }
      }
    },
    officesPerGrantee (to, from) {
      if (this.loaded === true) {
        this.removeOffices(Object.keys(from))
        this.addOffices()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';
.mapboxgl-canvas {
  border-radius: 1rem;
}
</style>